/**
 * CRM birthday celebration — frontend-only, independent of CRM gifts notify.
 * Shows once per calendar year when User/Contact.birthday matches today (local).
 */

const STORAGE_PREFIX = 'ewents.crm.birthdayCelebration.';

export const CRM_BIRTHDAY_MESSAGES = [
  "We're so happy you're here. Hope today feels as warm as you make our days.",
  'A big hug from the team. Enjoy every bit of today. You deserve it.',
  'Thank you for being here. Hope today fills with good things.',
  'Today we celebrate you. Have a beautiful day.',
  "Glad you're part of this. May your day be full of smiles.",
] as const;

/** Parse YYYY-MM-DD (or ISO) to local month/day. */
export function birthdayMonthDay(
  birthday: string | null | undefined
): { month: number; day: number } | null {
  if (!birthday?.trim()) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(birthday.trim());
  if (!m) return null;
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { month, day };
}

export function isBirthdayToday(
  birthday: string | null | undefined,
  now: Date = new Date()
): boolean {
  const md = birthdayMonthDay(birthday);
  if (!md) return false;
  return now.getMonth() + 1 === md.month && now.getDate() === md.day;
}

export function celebrationStorageKey(userId: string, year: number): string {
  return `${STORAGE_PREFIX}${userId}.${year}`;
}

export function hasSeenBirthdayCelebration(
  userId: string,
  year: number = new Date().getFullYear(),
  storage: Pick<Storage, 'getItem'> = localStorage
): boolean {
  if (!userId) return true;
  return storage.getItem(celebrationStorageKey(userId, year)) === '1';
}

export function markBirthdayCelebrationSeen(
  userId: string,
  year: number = new Date().getFullYear(),
  storage: Pick<Storage, 'setItem'> = localStorage
): void {
  if (!userId) return;
  storage.setItem(celebrationStorageKey(userId, year), '1');
}

export function shouldShowBirthdayCelebration(input: {
  userId: string | null | undefined;
  birthday: string | null | undefined;
  now?: Date;
  storage?: Pick<Storage, 'getItem'>;
}): boolean {
  const userId = input.userId?.trim();
  if (!userId) return false;
  const now = input.now ?? new Date();
  if (!isBirthdayToday(input.birthday, now)) return false;
  return !hasSeenBirthdayCelebration(userId, now.getFullYear(), input.storage ?? localStorage);
}

export function pickCelebrationMessageIndex(
  count: number,
  random: () => number = Math.random
): number {
  if (count <= 1) return 0;
  return Math.floor(random() * count);
}

export function pickCrmBirthdayMessage(random: () => number = Math.random): string {
  const i = pickCelebrationMessageIndex(CRM_BIRTHDAY_MESSAGES.length, random);
  return CRM_BIRTHDAY_MESSAGES[i]!;
}
